import { debounce } from 'lodash'
import { arrayOf, bool, node, oneOfType } from 'prop-types'
import React, { useState, useEffect } from 'react'
import { Configure, Index, InstantSearch } from 'react-instantsearch-dom'

import { track, getPageSource } from '@mc/track-event'
import { hasMatureContentDisabled } from '@mc/user-info'

import getCourses from './interactors/getCourses'
import searchClient from './repositories/searchClient'
import {
  getArticleIndex,
  getCampIndex,
  getChapterIndex,
  getCourseIndex,
} from '../getIndexNames'

const articleIndex = getArticleIndex()
const campIndex = getCampIndex()
const chapterIndex = getChapterIndex()
const courseIndex = getCourseIndex()

const debouncedQuerySegmentEvent = debounce((query) => {
  track('Search String Entered', {
    string: query,
    page_source: getPageSource(),
  })
}, 2000)

export const SearchContext = React.createContext()

const SearchProvider = ({ showCategories, showArticles, children }) => {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState(null)
  const [courses, setCourses] = useState(null)

  const cancelSearch = () => {
    track('Global Nav Search Canceled', {
      string: query,
    })
    setQuery('')
    setCategory(null)
  }

  const onCategoryClick = (selectedCategory) => {
    track('Global Nav Category Clicked', { category: selectedCategory })
    setCategory(selectedCategory)
    setQuery(selectedCategory)
  }

  useEffect(() => {
    if (query.length > 3) {
      debouncedQuerySegmentEvent(query)
    }
  }, [query])

  useEffect(() => {
    getCourses().then((fetchedCourses) => {
      setCourses(fetchedCourses)
    })
  }, [])

  return (
    <SearchContext.Provider
      value={{
        category,
        courses,
        query,
        showCategories,
        setQuery,
        cancelSearch,
        onCategoryClick,
      }}
    >
      <InstantSearch
        searchClient={searchClient}
        indexName={chapterIndex}
        searchState={{
          query,
        }}
        // helps clean up layout shifts when switching categories
        stalledSearchDelay={600}
      >
        {hasMatureContentDisabled() && (
          <Configure filters='mature_content:false' />
        )}
        {children}
        <Index indexName={courseIndex} />
        <Index indexName={campIndex} />
        {showArticles && <Index indexName={articleIndex} />}
      </InstantSearch>
    </SearchContext.Provider>
  )
}

SearchProvider.propTypes = {
  children: oneOfType([arrayOf(node), node]),
  // displays categories when no query is entered
  showCategories: bool,
  // include articles in search results
  showArticles: bool,
}

export default SearchProvider
